/* eslint-disable react-hooks/rules-of-hooks, @typescript-eslint/no-floating-promises, react-hooks/exhaustive-deps, @typescript-eslint/strict-boolean-expressions */

import { useRecoilValue } from 'recoil';
import { equityHubSymbolsSetState, hiroSymbolsState } from '../../../../states';
import { useHiroCtx } from '../../HiroProvider';
import { Box, Button, Stack, useTheme } from '@mui/material';
import useIsMobile from 'hooks/useIsMobile';
import React, { useCallback, useState } from 'react';
import { ProductType } from '../../../../types';
import { ColorMode } from '../../../../theme';
import { SymbolTab } from './SymbolTab';
import { readableProductType } from '../../../../util';
import useAuth from '../../../../hooks/auth/useAuth';
import { useSetSym } from '../../../../hooks';
import useUserDetails from '../../../../hooks/user/useUserDetails';

import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';

import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

interface SymbolTabsProps {
  sym?: string;
  productType: ProductType;
}

const MAX_NUM_TABS = 30;

export function SortableItem(props: {
  id: string;
  children: React.ReactNode | React.ReactNode[];
}) {
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id: props.id });
  const style = { transform: CSS.Transform.toString(transform), transition };

  return (
    <div
      ref={setNodeRef}
      style={style}
      key={props.id}
      {...attributes}
      {...listeners}
    >
      {props.children}
    </div>
  );
}

export const SymbolTabs = ({ sym, productType }: SymbolTabsProps) => {
  const theme = useTheme();
  const { setSym, getSym } = useSetSym();
  const { hiroTabs, userDetails, update } = useHiroCtx();
  const [deletedSyms, setDeletedSyms] = useState<string[]>([]);
  const selectedSym = sym ?? getSym();
  const { saveSgSettings } = useUserDetails();
  const isMobile = useIsMobile();
  const exceededMaxLength = hiroTabs.length >= MAX_NUM_TABS;
  const hiroSyms = useRecoilValue(hiroSymbolsState);
  const ehSyms = useRecoilValue(equityHubSymbolsSetState);
  const { productsWithAccess } = useAuth();

  const onClick = useCallback(
    (sym: string) => {
      setSym(sym, productType);
    },
    [setSym, productType],
  );

  const onDelete = useCallback(
    (sym: string) => {
      const newTabs = [...new Set(hiroTabs.filter((s: string) => s !== sym))];
      setDeletedSyms((syms) => [...syms, sym]);
      update({ hiroTabs: newTabs });
      saveSgSettings({ hiro: { tabs: newTabs } });
    },
    [hiroTabs, update, setSym, saveSgSettings, selectedSym],
  );

  const undoLastDeletion = useCallback(() => {
    if (deletedSyms.length > 0) {
      const newTabs = [
        ...new Set([...hiroTabs, deletedSyms[deletedSyms.length - 1]]),
      ];
      update({ hiroTabs: newTabs });
      setDeletedSyms((syms) => syms.slice(0, -1));
      saveSgSettings({ hiro: { tabs: newTabs } });
    }
  }, [hiroTabs, update, setSym, saveSgSettings]);

  const addTab = useCallback(() => {
    if (hiroTabs.length >= MAX_NUM_TABS) {
      return;
    }
    if (selectedSym != null && !hiroTabs.includes(selectedSym)) {
      const newTabs = [...new Set([...hiroTabs, selectedSym])];
      update({ hiroTabs: newTabs });
      saveSgSettings({ hiro: { tabs: newTabs } });
    }
  }, [hiroTabs, update, selectedSym, userDetails]);

  if (!productsWithAccess.has(productType)) {
    return null;
  }

  const productTypeHasSym = (sym: string) => {
    if (productType === ProductType.EQUITYHUB) {
      return ehSyms.has(sym);
    } else if (productType === ProductType.HIRO) {
      return hiroSyms.has(sym);
    }

    return true;
  };

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event;

    if (active && over && active.id !== over.id) {
      const oldIndex = hiroTabs.indexOf(active.id as string);
      const newIndex = hiroTabs.indexOf(over.id as string);

      const newTabs = arrayMove([...hiroTabs], oldIndex, newIndex);
      update({ hiroTabs: newTabs });
      saveSgSettings({ hiro: { tabs: newTabs } });
    }
  }

  return (
    <Box
      display="flex"
      width="100%"
      paddingRight={productType === ProductType.HIRO ? '15px' : '5px'}
      justifyContent="space-between"
      sx={{
        '& .active.MuiChip-root': {
          backgroundColor: `#0e7167 !important`,
          color: '#fff !important',
        },
        '& .notActive.MuiChip-root': {
          backgroundColor: `transparent !important`,
          ...(theme.colorMode === ColorMode.LIGHT
            ? {
                'span, & .MuiChip-deleteIcon': {
                  color: `${theme.palette.text.primary}`,
                },
              }
            : {}),
        },
        '.MuiChip-deleteIcon': {
          fontSize: '18px !important',
          transition: 'all 0.2s ease',
          ':hover': {
            color: `${theme.palette.text.secondary} !important`,
          },
        },
        '& .active .MuiChip-deleteIcon': {
          color: 'rgba(255,255,255,0.3) !important',
          ':hover': {
            color: 'rgba(255,255,255,0.2) !important',
          },
        },
      }}
    >
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        <SortableContext
          items={hiroTabs}
          strategy={verticalListSortingStrategy}
        >
          <Box
            display="flex"
            flexDirection="row"
            gap="7px"
            sx={{
              overflowX: 'auto',
              overflowY: 'hidden',
              width: '100%',
              paddingY: '8px',
              paddingLeft: '4px',
              paddingRight: '20px',
            }}
          >
            {hiroTabs.map((sym) => (
              <SortableItem id={sym} key={sym}>
                <SymbolTab
                  key={sym}
                  symbol={sym}
                  onDelete={onDelete}
                  onClick={onClick}
                  active={sym === selectedSym}
                  totalCount={hiroTabs.length}
                  disabledMessage={
                    productTypeHasSym(sym)
                      ? undefined
                      : `This symbol is not supported in ${readableProductType(
                          productType,
                        )}.`
                  }
                />
              </SortableItem>
            ))}
          </Box>
        </SortableContext>
      </DndContext>

      <Stack direction="row" alignItems="center" sx={{ paddingY: '8px' }}>
        {deletedSyms.length > 0 && (
          <Button
            sx={{
              textTransform: 'none',
              fontSize: isMobile ? '11px' : '13px',
              height: '30px',
              whiteSpace: 'nowrap',
              textAlign: 'center',
            }}
            onClick={undoLastDeletion}
          >
            Restore {deletedSyms[deletedSyms.length - 1]}
          </Button>
        )}
        {selectedSym != null && !hiroTabs.includes(selectedSym) && (
          <Button
            sx={{
              textTransform: 'none',
              fontSize: isMobile ? '11px' : '13px',
              height: '30px',
              whiteSpace: 'nowrap',
              textAlign: 'center',
            }}
            disabled={exceededMaxLength}
            onClick={addTab}
          >
            {exceededMaxLength ? 'Too many tabs' : `+ Add Tab`}
          </Button>
        )}
      </Stack>
    </Box>
  );
};
